import { useQuery } from "@tanstack/react-query";

import { api } from "@/api/client";
import type { AdminAuditEvent } from "@/api/admin/users";
import { useMe } from "@/api/me";

// ---- Admin inspection (read-only; every authenticated user) ---------

export type FilesystemDirStats = {
  exists: boolean;
  /** Space taken on disk (allocated blocks, like ``du``): each inode
   *  once across all rows, symlinks not followed. */
  bytes: number;
  /** Sum of file sizes; runs well under ``bytes`` on trees of many
   *  small files. */
  apparent_bytes?: number;
  /** The walk hit its time budget; the counters are a lower bound. */
  truncated?: boolean;
  /** Directories or entries the scan could not read (left out of the
   *  counters), with up to three example paths. */
  skipped?: number;
  skipped_paths?: string[];
  files: number;
  /** Count of immediate-child entries — answers "how many sessions
   *  have attachments" / "how many shared worktrees" without
   *  needing to drill into the tree. */
  top_level_dirs: number;
  top_level_files: number;
  path: string;
  /** Human-readable description of what this dir is for. Surfaced
   *  in the row tooltip so operators don't have to grep the code
   *  to know why an empty dir exists. */
  purpose?: string;
  /** ``true`` when "0 files" is normal — dirs that only get
   *  populated under specific deploy configurations (codex-home
   *  on central-billing deploys, rollouts on claude-only
   *  deploys, etc.). UI dampens the empty-state visual. */
  commonly_empty?: boolean;
};

export type FilesystemDisk = {
  path: string;
  ok: boolean;
  total_bytes?: number;
  used_bytes?: number;
  free_bytes?: number;
  /** ``major:minor`` of the filesystem. */
  id?: string;
  /** The mount source (or path): stable across reboots; ``trend.disks``
   *  is keyed by it. */
  key?: string;
  /** Block device (or "overlay") from the mount table. */
  source?: string | null;
  fstype?: string | null;
  /** The container's own writable layer, not a data volume. */
  container_layer?: boolean;
  /** A data disk holding repos or worktrees with less free space than
   *  RW_MIN_FREE_DISK_GB: new clones and checkouts are refused. */
  below_floor?: boolean;
  /** Same disks, with less than twice that free. */
  near_floor?: boolean;
  mount_points?: string[];
  /** Names (keys of ``dirs`` / ``external``) of the rows on this disk. */
  dirs?: string[];
  scanned_bytes?: number;
  /** Used space the rows don't explain: database volumes, images,
   *  other containers' volumes on a shared host disk. */
  other_bytes?: number;
};

export type FsOrgLabel = {
  org_id: string | null;
  org_name: string | null;
  org_slug: string | null;
  org_deleted: boolean;
};

export type FsOrgUsage = FsOrgLabel & {
  /** Bytes per data tree (repos, worktrees, sessions, agent-home, …). */
  trees: Record<string, number>;
  total_bytes: number;
};

export type FsTopWorktree = FsOrgLabel & {
  id: string;
  repository: string | null;
  branch: string | null;
  status: string;
  bytes: number;
  last_used_at: string | null;
};

export type FsTopSession = FsOrgLabel & {
  id: string;
  title: string | null;
  bytes: number;
  deleted: boolean;
  last_message_at: string | null;
  trees: Record<string, number>;
};

export type FsOrphan = Partial<FsOrgLabel> & {
  tree: string;
  path: string;
  bytes: number;
  reason: "unknown_owner" | "org_deleted" | "session_missing";
};

export type FsSweep = {
  key: string;
  enabled?: boolean;
  days?: number;
  /** ``null`` when the sweep's candidates can't be forecast from disk. */
  count: number | null;
  bytes: number | null;
  budget_bytes?: number;
  used_bytes?: number;
};

export type FsCleanup = {
  interval_seconds: number;
  last_tick_at: string | null;
  next_tick_at: string | null;
  sweeps: FsSweep[];
};

export type FsDiskTrend = {
  /** ``null`` until there is a sample at least a day old. */
  growth_bytes_per_day: number | null;
  days_until_full: number | null;
  window_days: number | null;
  history: { day: string; used: number | null }[];
};

export type FilesystemInspect = {
  repo_root: string;
  /** The fullest disk holding Repoworks data (Overview warning). */
  disk: FilesystemDisk;
  /** Every filesystem the rows live on, data disks first. */
  disks?: FilesystemDisk[];
  scanned_at?: string;
  orgs?: FsOrgUsage[];
  top_worktrees?: FsTopWorktree[];
  top_sessions?: FsTopSession[];
  orphans?: { count: number; total_bytes: number; items: FsOrphan[] };
  cleanup?: FsCleanup;
  /** From the daily samples: per disk (by ``key``) and per row. */
  trend?: {
    disks: Record<string, FsDiskTrend>;
    rows: Record<string, number | null>;
  };
  dirs: Record<string, FilesystemDirStats>;
  /** Out-of-repo-root paths the operator should know about (MCP
   *  secret staging dirs that live under ``/var/`` and ``/etc/``). */
  external?: Record<string, FilesystemDirStats>;
  /** Database / cache info. Stores live in their own container
   *  volumes (or in-memory for redis), but each carries a ``size``
   *  block sourced via cheap catalog queries (``pg_database_size``
   *  / redis ``INFO memory``). ``size.ok=false`` surfaces the
   *  error so a wedged store reads as "size unknown" instead of
   *  hiding the row. */
  databases?: Record<
    string,
    {
      kind: string;
      container_volume: string | null;
      purpose: string;
      size: {
        ok: boolean;
        bytes?: number;
        peak_bytes?: number;
        max_bytes?: number;
        error?: string;
      };
    }
  >;
  disk_budget_bytes: number;
  /** RW_MIN_FREE_DISK_GB in bytes; 0 = no floor. */
  min_free_bytes?: number;
};

export type WorktreeInspect = {
  id: string;
  path: string;
  exists_on_disk: boolean;
  /** Space on disk (allocated blocks, like ``du``). */
  bytes: number;
  /** Sum of file sizes. */
  apparent_bytes?: number | null;
  files: number;
  branch: string | null;
  status: string;
  last_used_at: string | null;
  created_at: string;
  updated_at: string;
  session_count: number;
  repository: {
    id: string;
    path_with_namespace: string;
    bare_path: string;
  } | null;
};

/** Provider-agnostic name for an agent-runtime / MCP subprocess
 *  the operator dashboard surfaces. The legacy ``CodexProcess``
 *  alias points at the same shape so existing callers keep
 *  working through one release cycle. */
export type AgentProcess = {
  pid: number;
  argv: string[];
  cwd: string | null;
  name?: string;
  state?: string;
  rss_bytes?: number;
  uid?: number;
};
/** @deprecated use ``AgentProcess`` — preserved for one release. */
export type CodexProcess = AgentProcess;

export type AgentRuntimeHealth = {
  providers: Array<{
    slug: string;
    display_name: string;
    enabled: boolean;
    binary: string;
    binary_exists: boolean;
    version: string | null;
    version_ok: boolean;
    version_error: string | null;
    default_model: string;
    credential_mode: string;
  }>;
  /** Sessions with a turn running; ``long_running`` past ``long_minutes``. */
  turns?: {
    running: number;
    oldest_started_at: string | null;
    long_running: number;
    long_minutes: number;
  };
  /** RQ jobs per queue (waiting, running, failed). */
  jobs?:
    | {
        ok: true;
        queues: Record<
          string,
          {
            queued: number;
            started: number;
            /** Still in RQ's failed registry (kept a year). */
            failed: number;
            /** Failed in the last 24 h: what the UI colours by. */
            failed_24h?: number;
          }
        >;
      }
    | { ok: false; error: string };
};

export type SessionInspectRow = {
  id: string;
  title: string;
  status: string;
  intent: string;
  mode: string | null;
  user_id: string;
  owner_email: string | null;
  owner_display_name: string | null;
  active_turn_id: string | null;
  /** ISO start of the running turn, when one runs. */
  active_turn_started_at?: string | null;
  /** Number of participants with an unsent draft on this session. */
  draft_count: number;
  /** Sum of those drafts' character counts. */
  draft_chars: number;
  last_message_at: string | null;
  created_at: string;
  updated_at: string;
  /** Soft-delete stamp; non-null only when listed with
   *  ``includeDeleted``. Such rows open read-only. */
  deleted_at: string | null;
  secondary_repository_refs?: Array<{
    slug: string | null;
    ssh_url: string | null;
  }>;
};

export type SessionsInspectParams = {
  owner?: string;
  status?: string[];
  intent?: string[];
  /** Only sessions with a turn currently running. */
  activeTurn?: boolean;
  /** Only sessions with ≥1 unsent participant draft. */
  hasDraft?: boolean;
  createdAfter?: string;
  createdBefore?: string;
  /** Also list soft-deleted sessions (platform admins). */
  includeDeleted?: boolean;
  limit?: number;
  offset?: number;
};

export type SessionsInspect = {
  counts: Record<string, number>;
  /** The current filtered + paginated page. */
  items: SessionInspectRow[];
  /** Total rows matching the filters (for pagination). */
  total: number;
  limit: number;
  offset: number;
};

/** ``GET /admin/overview`` — one shape for both admin kinds. Scoped to
 *  the active organisation for an org admin (or a platform admin who
 *  picked one); ``scope === "platform"`` when a platform admin reads
 *  across every org. Breakdowns are keyed by whatever intents and
 *  providers the rows carry, so the dashboard never hardcodes either. */
export type OverviewSummary = {
  scope: "org" | "platform";
  members: { total: number; without_agent_credential: number };
  sessions: {
    active: number;
    created_7d: number;
    by_intent_7d: Record<string, number>;
    by_provider_7d: Record<string, number>;
    failed_turns_24h: number;
  };
  /** Rendered library documents created in the last 7 days, one per
   *  rendered version, as the library lists them. */
  documents_7d: number;
  /** Audit events of the last 24 hours. An org view counts only the
   *  events attributed to the org, as the Audit tab lists them. */
  audit_events_24h: number;
  recent_audit: Array<
    Pick<
      AdminAuditEvent,
      "id" | "occurred_at" | "actor_kind" | "action" | "resource_kind" | "resource_id"
    >
  >;
  /** Usage in the scope. A figure the viewer may not see is ``null``:
   *  cost and tokens reach only a global admin (the Costs tab's rule),
   *  credits only while credits are on and visible to the viewer (the
   *  Credit Ledger's rule). Optional: an older server sends none. */
  usage?: OverviewUsage;
};

export type OverviewUsage = {
  turns_7d: number;
  tokens_7d: number | null;
  cost_usd_micros_7d: number | null;
  cost_usd_micros_30d: number | null;
  credits_used_micros_7d: number | null;
  credits_used_micros_30d: number | null;
  micros_per_credit: number | null;
};

/** ``GET /admin/overview/activity`` — one entry per UTC day from a
 *  Monday 52 weeks back through today. ``credits_micros`` is present
 *  only when ``credits`` is true. */
export type OverviewActivity = {
  start: string;
  end: string;
  timezone: string;
  /** Distinct turns over the whole window. Not the sum of ``days``: a
   *  turn straddling UTC midnight is a turn on both days but one turn.
   *  Optional: an older server sends none. */
  total_turns?: number;
  credits: boolean;
  micros_per_credit: number | null;
  days: Array<{ day: string; turns: number; credits_micros?: number }>;
};

export function useAdminOverview() {
  // The server scopes on the pinned org; the slug goes into the key so
  // switching tenants never serves the previous org's numbers.
  const activeOrgSlug = useMe().data?.admin_scope?.active_org_slug ?? null;
  return useQuery({
    queryKey: ["admin", "overview", activeOrgSlug],
    queryFn: async () =>
      (await api.get<OverviewSummary>("/admin/overview")).data,
    refetchInterval: 30_000,
  });
}

export function useAdminOverviewActivity() {
  const activeOrgSlug = useMe().data?.admin_scope?.active_org_slug ?? null;
  return useQuery({
    queryKey: ["admin", "overview", "activity", activeOrgSlug],
    queryFn: async () =>
      (await api.get<OverviewActivity>("/admin/overview/activity")).data,
    // A year of days changes by one square at a time; the summary's
    // 30-second poll would only re-run the heaviest query on the page.
    staleTime: 5 * 60_000,
  });
}

// The server walks every clone and checked-out tree for this and
// caches the result for five minutes; polling only re-reads the cache
// in between.
export function useFilesystemInspect() {
  return useQuery({
    queryKey: ["admin-inspect", "filesystem"],
    queryFn: async () =>
      (await api.get<FilesystemInspect>("/admin/inspect/filesystem")).data,
    refetchInterval: 60_000,
  });
}

/** The light variant for the Overview's disk warning: disks, rows and
 *  trend from the same cached scan, without the per-org insights or the
 *  database sizes. */
export function useFilesystemSummary() {
  return useQuery({
    queryKey: ["admin-inspect", "filesystem", "light"],
    queryFn: async () =>
      (
        await api.get<FilesystemInspect>("/admin/inspect/filesystem", {
          params: { light: true },
        })
      ).data,
    refetchInterval: 60_000,
  });
}

/** `stats: false` skips the per-worktree disk walk — the Overview only
 *  counts rows, and walking every tree every 15 s for a number was the
 *  single heaviest thing the admin page did. */
export function useWorktreesInspect(opts: { stats?: boolean } = {}) {
  const stats = opts.stats ?? true;
  return useQuery({
    queryKey: ["admin-inspect", "worktrees", stats],
    queryFn: async () =>
      (
        await api.get<WorktreeInspect[]>("/admin/inspect/worktrees", {
          params: { stats },
        })
      ).data,
    refetchInterval: stats ? 30_000 : 15_000,
  });
}

/** Provider-agnostic agent-runtime + MCP process probe.
 *
 *  Polled at 30 s — process state rarely changes (only on
 *  deploy / manual restart) and the ``/proc`` walk on the
 *  backend is cheap but compounds across admin tabs. The
 *  earlier 5 s cadence saw 100+ admins × 12 polls/min hammer
 *  the same endpoint that runs subprocess version probes for
 *  every detected runtime, which was a significant chunk of
 *  the deploy-time SPA-feels-sluggish symptom.
 *
 *  React Query's default ``refetchIntervalInBackground: false``
 *  means polling already pauses on hidden tabs — no extra
 *  wiring needed for that. */
export function useAgentProcesses() {
  return useQuery({
    queryKey: ["admin-inspect", "agent-processes"],
    queryFn: async () =>
      (await api.get<AgentProcess[]>("/admin/inspect/agent-processes")).data,
    refetchInterval: 30_000,
  });
}
/** @deprecated use ``useAgentProcesses`` — kept for one release. */
export const useCodexProcesses = useAgentProcesses;

export function useAgentRuntimeHealth() {
  return useQuery({
    queryKey: ["admin-inspect", "agent-runtime"],
    queryFn: async () =>
      (await api.get<AgentRuntimeHealth>("/admin/inspect/agent-runtime")).data,
    refetchInterval: 30_000,
  });
}

export function useSessionsInspect(params: SessionsInspectParams = {}) {
  // Build the query string from the filter/pagination params; the
  // serialised params are also the cache key so each filter combo +
  // page is cached independently. ``status``/``intent`` repeat.
  const search = new URLSearchParams();
  if (params.owner) search.set("owner", params.owner);
  for (const s of params.status ?? []) search.append("status", s);
  for (const i of params.intent ?? []) search.append("intent", i);
  if (params.activeTurn) search.set("active_turn", "true");
  if (params.hasDraft) search.set("has_draft", "true");
  if (params.includeDeleted) search.set("include_deleted", "true");
  if (params.createdAfter) search.set("created_after", params.createdAfter);
  if (params.createdBefore) search.set("created_before", params.createdBefore);
  if (params.limit != null) search.set("limit", String(params.limit));
  if (params.offset != null) search.set("offset", String(params.offset));
  const qs = search.toString();
  return useQuery({
    queryKey: ["admin-inspect", "sessions", qs],
    queryFn: async () =>
      (
        await api.get<SessionsInspect>(
          `/admin/inspect/sessions${qs ? `?${qs}` : ""}`,
        )
      ).data,
    // Nothing in the response changes faster than a 30 s lag is
    // perceptible, and the backend does a non-trivial COUNT + page
    // on every fetch.
    refetchInterval: 30_000,
  });
}
